import React from "react";
import PropTypes from "prop-types";
//Material imports
import { withStyles } from "@material-ui/core/styles";
import MUIFab from "@material-ui/core/Fab";
import Grow from "@material-ui/core/Grow";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import Close from "@material-ui/icons/Close";
import Badge from "@material-ui/core/Badge";
//Limitless imports
import { ClientContext } from "@limitless/common/utils";
import { useMobileDetectionContext } from "hooks/mobile-detection/MobileDetectionContext";
import AnimatedFab from "./AnimatedFab";
import ExpandableFab from "./ExpandableFab";

const CLOSE_ICON_MAP = {
    chat: ChatBubbleOutline,
    close: Close
};

const Fab = ({ classes, isLLMVisible, isMobileDevice, onClick, unReadMessage }) => {
    const { getCustomOffsetStyles } = useMobileDetectionContext();
    const [showFabText, setShowFabText] = React.useState(false);
    const CloseIcon = CLOSE_ICON_MAP[ClientContext._fabCloseButtonStyle];
    const fabIcon = isLLMVisible ? (
        <CloseIcon className={classes.chatIcon} />
    ) : (
        <ChatBubbleOutline className={classes.chatIcon} />
    );

    const renderFABBadge = (fab, animated = false) => {
        if (unReadMessage && !isLLMVisible) {
            const badgeClasses = `${classes.badgeRoot} ${animated ? classes.animatedBadge : ""}`;
            const offsets = getCustomOffsetStyles(42, animated ? 6 : -17);

            return (
                <>
                    {fab}
                    <Badge badgeContent={" "} classes={{ root: badgeClasses }} style={offsets} />
                </>
            );
        }

        return fab;
    };

    let fabText;
    if (isMobileDevice && ClientContext._fabMobileText) {
        fabText = ClientContext._fabMobileText;
    }
    if (!isMobileDevice && ClientContext._fabText) {
        fabText = ClientContext._fabText;
    }

    if (fabText) {
        const FabTextComponent = ClientContext._expandableFab ? ExpandableFab : AnimatedFab;

        const animatedFab = (
            <FabTextComponent
                classes={classes}
                isMobileDevice={isMobileDevice}
                onClick={onClick}
                fabText={fabText}
                CloseIcon={CloseIcon}
                openIcon={fabIcon}
                color="primary"
                aria-label="Open Live Messenger"
                component="div"
                disableRipple
            />
        );

        return renderFABBadge(animatedFab, true);
    }

    return (
        <Grow in timeout={600} appear={ClientContext._animation}>
            <MUIFab
                variant="round"
                color="primary"
                aria-label="Open Live Messenger"
                className={`${classes.button} ${isMobileDevice && isLLMVisible ? classes.hidden : ""} ${
                    classes.iconButton
                }`}
                classes={{ root: classes.fabRoot }}
                onClick={onClick}
                component="div"
                disableRipple
                onMouseOver={() => setShowFabText(true)}
                onMouseOut={() => setShowFabText(false)}
            >
                {fabText && showFabText && <span className={classes.fabText}> {fabText} </span>}
                {renderFABBadge(fabIcon)}
            </MUIFab>
        </Grow>
    );
};

/** styles for both fab and animated fab as many are shared */
const styles = theme => {
    const fabStyles = {
        button: {
            margin: 8,
            position: "fixed",
            bottom: ClientContext._bottomOffset,
            right: ClientContext._rightOffset,
            zIndex: "21400053001",
            textTransform: "none",
            height: 48
        },
        iconButton: {
            width: 48,
            height: 48
        },
        fabText: {
            padding: "0 22px",
            whiteSpace: "nowrap"
        },
        chatIcon: {
            transform: "scaleX(-1)"
        },
        hidden: {
            visibility: "hidden"
        },
        badgeRoot: {
            top: -5,
            height: 15,
            width: 15,
            borderRadius: 15,
            position: "absolute",
            zIndex: "21400053002",
            minWidth: "0 !important",
            backgroundColor: "#EF5350"
        },
        "@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)": {
            badgeRoot: {
                top: "auto",
                bottom: ClientContext._bottomOffset + 14,
                right: Number(ClientContext._rightOffset.replace("px", "")) + 28,
                position: "fixed"
            }
        },
        animatedBadge: {
            top: "unset",
            position: "fixed"
        },
        fabRoot: {}
    };

    let fabBorderColor = theme.palette.contrast.main;

    if (theme.palette.fab) {
        fabStyles.fabRoot = {
            backgroundColor: theme.palette.fab.backgroundColor,
            "&:hover": {
                backgroundColor: theme.palette.fab.hoverBackgroundColor
            },
            "&:active": {
                boxShadow: "unset"
            }
        };
        fabBorderColor = theme.palette.fab.borderColor;
        //override
        if (theme.palette.fab.textColor) {
            fabStyles.fabText.color = theme.palette.fab.textColor;
            fabStyles.chatIcon.color = theme.palette.fab.textColor;
        }
    }

    if (ClientContext._fabButtonStyle === "flat") {
        fabStyles.fabRoot.boxShadow = "unset";
        fabStyles.fabRoot.border = `solid 1px ${fabBorderColor}`;
    }

    return fabStyles;
};

export default withStyles(styles)(Fab);

Fab.propTypes = {
    classes: PropTypes.object.isRequired,
    isLLMVisible: PropTypes.bool.isRequired,
    isMobileDevice: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    unReadMessage: PropTypes.bool.isRequired
};
